<template>
    <!-- Modal -->
    <transition name="slide-up">
        <div v-if="isVisible" class="release-notes-modal">
            <!-- Close button -->
            <button
                type="button"
                class="release-notes-close-btn icon-btn icon-btn-white"
                @click="closeModal"
            >
                <i class="fi fi-x"></i>
            </button>

            <div class="release-notes-container">
                <!-- Left Sidebar with tabs -->
                <div class="release-notes-sidebar">
                    <!-- Header with icon and title -->
                    <div class="release-notes-header">
                        <div class="release-notes-icon">
                            <img src="@/assets/images/icons/filled-star.svg" alt="Star Icon" />
                        </div>
                        <h2 class="release-notes-title">What's New</h2>
                    </div>

                    <!-- Tab list -->
                    <div class="release-notes-tabs">
                        <button
                            v-for="tab in tabOptions"
                            :key="tab.value"
                            type="button"
                            :class="['release-notes-tab', { active: tab.value === selectedTab }]"
                            @click="selectTab(tab.value)"
                        >
                            {{ tab.text }}
                        </button>
                    </div>
                </div>

                <!-- Right Content Area -->
                <div class="release-notes-content">
                    <!-- Scrollable content -->
                    <div class="release-notes-scrollable">
                        <div
                            v-for="note in filteredReleaseNotes"
                            :key="note.id"
                            class="release-note-item"
                        >
                            <div v-if="note.image" class="release-note-image-container">
                                <ImageExpandable
                                    :image-id="note.image.id"
                                    :alt="note.subject_line"
                                    class="release-note-image"
                                    :no-thumb="true"
                                />
                            </div>

                            <!-- Subject line -->
                            <h3 class="release-note-subject">{{ note.subject_line }}</h3>

                            <!-- Description -->
                            <div class="release-note-description" v-html="note.description"></div>
                        </div>
                    </div>

                    <!-- Action buttons -->
                    <div class="release-notes-actions">
                        <button
                            type="button"
                            class="btn btn-white"
                            @click="closeModal"
                        >
                            {{ isPreviewMode ? 'Close' : 'Continue to Dashboard' }}
                        </button>
                        <button
                            v-if="hasLink"
                            type="button"
                            class="btn btn-primary"
                            @click="handleTryNow"
                        >
                            Try Now
                        </button>
                    </div>

                    <!-- View all link -->
                    <div v-if="!isPreviewMode" class="release-notes-footer">
                        <span
                            class="span-link"
                            @click="viewAllReleaseNotes"
                        >
                            View All Release Notes
                            <i class="fi fi-chevron-right ml-1" style="font-size: 16px;"></i>
                        </span>
                    </div>
                </div>
            </div>
        </div>
    </transition>

    <!-- Overlay -->
    <div v-if="isVisible" class="release-notes-overlay" @click="closeModal"></div>
</template>

<script>
import ImageExpandable from '@/components/ImageExpandable.vue';

export default {
    name: 'ReleaseNotesModal',
    components: {
        ImageExpandable,
    },
    data() {
        return {
            isVisible: false,
            releaseNotes: [],
            selectedTab: '',
            previousBodyOverflow: null,
            isLoading: false,
            isPreviewMode: false,
        };
    },
    computed: {
        shouldCheckVisibility() {
            return (
                this.$route.meta?.requiresAuth !== false &&
                this.$userStore?.isAuthenticated &&
                this.$route &&
                this.$route.matched.length > 0
            );
        },
        tabOptions() {
            const uniqueTabs = [...new Set(this.releaseNotes.map(note => note.tab_title))];
            
            return uniqueTabs.map(title => ({
                text: title,
                value: title.toLowerCase().replace(/\s+/g, '-'),
            }));
        },
        filteredReleaseNotes() {
            if (!this.selectedTab || this.tabOptions.length === 0) {
                return this.releaseNotes;
            }

            const selectedTabOption = this.tabOptions.find(tab => tab.value === this.selectedTab);
            if (!selectedTabOption) {
                return this.releaseNotes;
            }

            return this.releaseNotes.filter(
                note => note.tab_title === selectedTabOption.text
            );
        },
        hasLink() {
            return this.filteredReleaseNotes.some(note => note.link && note.link.trim() !== '');
        },
    },
    methods: {
        async fetchUnseenReleaseNotes() {
            if (this.isLoading) return;
            
            this.isLoading = true;

            try {
                const response = await this.$getData('release-note/unseen');

                if (response?.success && response?.data?.length > 0) {
                    this.releaseNotes = response.data;
                    
                    if (this.tabOptions.length > 0) {
                        this.selectedTab = this.tabOptions[0].value;
                    }
                    
                    this.isVisible = true;
                } else {
                    this.releaseNotes = [];
                    this.isVisible = false;
                }
            } catch (error) {
                console.error('Error fetching unseen release notes:', error);
                this.$alertStore?.pushError('Failed to fetch release notes');
            } finally {
                this.isLoading = false;
            }
        },
        async markNotesAsViewed() {
            if (!this.releaseNotes || this.releaseNotes.length === 0) return;

            // Capture IDs immediately before any state changes
            const releaseNoteIds = this.releaseNotes.map(note => note.id);

            try {
                // Return the promise so it can be awaited
                return await this.$postData('release-note/mark-viewed', {
                    release_note_ids: releaseNoteIds,
                });
            } catch (error) {
                console.error('Error marking release notes as viewed:', error);
            }
        },
        async closeModal() {
            if (!this.isPreviewMode) {
                await this.markNotesAsViewed();
            }
            this.isVisible = false;
            this.releaseNotes = [];
            this.selectedTab = '';
            this.isPreviewMode = false;
        },
        selectTab(value) {
            this.selectedTab = value;
        },
        openPreview(releaseNote) {
            if (!releaseNote) return;
            this.releaseNotes = [{ ...releaseNote }];
            if (this.tabOptions.length > 0) {
                this.selectedTab = this.tabOptions[0].value;
            }
            this.isPreviewMode = true;
            this.isVisible = true;
        },
        async handleTryNow() {
            // 1. Capture IDs and link first
            const link = this.filteredReleaseNotes.find(n => n.link)?.link;
            
            // 2. Save progress to DB (skip in preview mode so link can be tested without marking as viewed)
            if (!this.isPreviewMode) {
                await this.markNotesAsViewed();
            }
            
            // 3. Clear UI
            this.isVisible = false;
            this.releaseNotes = [];
            this.selectedTab = '';
            this.isPreviewMode = false;

            // 4. Navigate
            if (link) {
                if (link.startsWith('http')) {
                    try {
                        const url = new URL(link);
                        // If it's the same origin, use Vue Router for SPA navigation
                        if (url.origin === window.location.origin) {
                            this.$router.push(url.pathname + url.search + url.hash);
                        } else {
                            // External link - open in new tab
                            window.open(link, '_blank');
                        }
                    } catch (e) {
                        window.open(link, '_blank');
                    }
                } else {
                    this.$router.push(link);
                }
            }
        },
        async viewAllReleaseNotes() {
            await this.markNotesAsViewed();
            this.isVisible = false;
            this.$router.push('/release-notes');
        },
        checkAndShowReleaseNotes() {
            if (this.isVisible) return;
            if (!this.shouldCheckVisibility) return;
            this.fetchUnseenReleaseNotes();
        },
    },
    watch: {
        isVisible(newValue) {
            if (typeof document === 'undefined') return;

            if (newValue) {
                this.previousBodyOverflow = document.body.style.overflow || '';
                document.body.style.overflow = 'hidden';
            } else {
                if (this.previousBodyOverflow !== null) {
                    document.body.style.overflow = this.previousBodyOverflow;
                    this.previousBodyOverflow = null;
                }
            }
        },
        '$userStore.user': {
            handler() {
                this.checkAndShowReleaseNotes();
            },
        },
        '$userStore.isAuthenticated': {
            handler() {
                this.checkAndShowReleaseNotes();
            },
            immediate: true,
        },
        '$route': {
            handler(newRoute, oldRoute) {
                if (this.isVisible && oldRoute && newRoute.path !== oldRoute.path) {
                    this.closeModal();
                }
            },
        },
    },
    mounted() {
        if (typeof document === 'undefined') return;

        if (this.isVisible) {
            this.previousBodyOverflow = document.body.style.overflow || '';
            document.body.style.overflow = 'hidden';
        }

        this.$nextTick(() => {
            setTimeout(() => {
                this.checkAndShowReleaseNotes();
            }, 3000);
        });
    },
    beforeUnmount() {
        if (typeof document === 'undefined') return;

        if (this.previousBodyOverflow !== null) {
            document.body.style.overflow = this.previousBodyOverflow;
        } else {
            if (document.body.style.overflow === 'hidden') {
                document.body.style.overflow = '';
            }
        }
    },
};
</script>

<style scoped>
/* Overlay */
.release-notes-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.1);
    z-index: 1059;
}

/* Modal transition */
.slide-up-enter-active,
.slide-up-leave-active {
    transition: all 0.3s ease;
}

.slide-up-enter-from,
.slide-up-leave-to {
    opacity: 0;
    transform: translateY(20px);
}

.release-notes-modal {
    background-color: white;
    max-height: 85vh;
    z-index: 1062;
    width: 800px;
    max-width: 95%;
    height: 715px;
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    border-radius: var(--radius-default);
    padding: var(--space-6);
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
}

.release-notes-close-btn {
    position: absolute;
    top: 12px;
    right: 12px;
    z-index: 10;
}

.release-notes-container {
    display: flex;
    gap: var(--space-4);
    height: 100%;
    min-height: 0;
}

/* Left Sidebar */
.release-notes-sidebar {
    flex-shrink: 0;
    width: 220px;
    display: flex;
    flex-direction: column;
    gap: var(--space-4);
    border-right: 1px solid var(--neutral-600);
    padding-right: var(--space-4);
}

/* Header */
.release-notes-header {
    display: flex;
    align-items: center;
    gap: var(--space-2);
}

.release-notes-icon {
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.release-notes-icon img {
    width: 24px;
    height: 24px;
}

.release-notes-title {
    font-family: 'Montserrat', sans-serif;
    font-size: 1.375rem;
    font-weight: 700;
    line-height: var(--space-7);
    color: var(--neutral-900);
    margin: 0;
}

/* Tabs */
.release-notes-tabs {
    display: flex;
    flex-direction: column;
    gap: 0;
}

.release-notes-tab {
    padding: var(--space-1) var(--space-2);
    background: transparent;
    border: none;
    border-radius: var(--radius-sm);
    text-align: left;
    font-family: 'Inter', sans-serif;
    font-size: 0.875rem;
    font-weight: 400;
    line-height: 1.25rem;
    color: var(--neutral-800);
    cursor: pointer;
    transition: all 0.2s ease;
    position: relative;
}

.release-notes-tab:hover {
    background-color: #F7F7F7;
}

.release-notes-tab.active {
    background-color: #F1F1F1;
    font-weight: 600;
    color: var(--neutral-900);
}

.release-notes-tab:focus {
    outline: none;
}

/* Right Content Area */
.release-notes-content {
    flex: 1;
    display: flex;
    flex-direction: column;
    min-width: 0;
    gap: var(--space-5 );
    padding-left: var(--space-2);
}

/* Scrollable content */
.release-notes-scrollable {
    flex: 1;
    overflow-y: auto;
    overflow-x: hidden;
    min-height: 0;
    padding-right: var(--space-2);
}

/* Scrollbar */
.release-notes-scrollable::-webkit-scrollbar {
    width: 4px;
}

.release-notes-scrollable::-webkit-scrollbar-track {
    background: transparent;
}

.release-notes-scrollable::-webkit-scrollbar-thumb {
    background: var(--neutral-500);
    border-radius: var(--radius-sm);
}

.release-notes-scrollable::-webkit-scrollbar-thumb:hover {
    background: var(--neutral-600);
}

/* Release Note Item */
.release-note-item {
    display: flex;
    flex-direction: column;
}

.release-note-item:not(:last-child) {
    margin-bottom: var(--space-6);
    padding-bottom: var(--space-6);
    border-bottom: 1px solid var(--neutral-300);
}

.release-note-image-container {
    width: 100%;
    margin-bottom: var(--space-5);
    border-radius: var(--radius-default);
    overflow: hidden;
    border: 1px solid var(--primary-300);
    aspect-ratio: 16/9;
    background-color: var(--white);
}

.release-note-subject {
    font-family: 'Montserrat', sans-serif;
    font-size: 1.375rem;
    font-weight: 700;
    line-height: var(--space-7);
    color: var(--neutral-900);
    margin: 0;
    margin-bottom: var(--space-2);
}

.release-note-description {
    font-family: 'Inter', sans-serif;
    font-size: 1rem;
    font-weight: 400;
    line-height: 1.625rem;
    color: var(--neutral-800);
}

.release-note-description :deep(p) {
    line-height: 1.625rem;
    margin-bottom: var(--space-3);
}

.release-note-description :deep(p:last-child) {
    margin-bottom: 0;
}

.release-note-description :deep(ul),
.release-note-description :deep(ol) {
    line-height: 1.625rem;
    margin-bottom: var(--space-3);
    padding-left: var(--space-5);
}

.release-note-description :deep(li) {
    line-height: 1.625rem;
    margin-bottom: var(--space-2);
}

.release-note-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
    object-position: center;
}

/* Actions */
.release-notes-actions {
    display: flex;
    justify-content: flex-end;
    gap: var(--space-3);
}

.release-notes-actions .btn {
    flex: 0 0 auto;
}

/* Footer */
.release-notes-footer {
    display: flex;
    justify-content: flex-end;
}

/* Mobile Responsive */
@media (max-width: 991.98px) {
    .release-notes-modal {
        width: calc(100% - 32px);
        max-width: 500px;
        height: auto; 
        padding: var(--space-4);
        display: flex;
        flex-direction: column;
        overflow: hidden;
        border-radius: var(--radius-default);
    }

    .release-notes-close-btn {
        top: var(--space-4);
        right: var(--space-4);
    }

    .release-notes-container {
        flex-direction: column;
        gap: var(--space-3);
        height: 100%;
        min-height: 0;
        overflow: hidden;
    }

    .release-notes-content {
        overflow-y: auto;
        padding-left: 0;
        display: flex;
        flex-direction: column;
        gap: var(--space-4);
        padding-bottom: var(--space-1);
    }

    .release-notes-scrollable {
        padding-right: 0;
    }

    .release-notes-sidebar {
        width: 100%;
        border-right: none;
        border-bottom: 1px solid var(--neutral-600);
        padding-right: 0;
        padding-bottom: var(--space-3);
        flex-shrink: 0;
    }

    .release-notes-tabs {
        max-height: 15vh;
        overflow-y: auto;
    }

    .release-notes-title {
        font-size: 1.125rem;
    }

    .release-note-subject {
        font-size: 1.125rem;
    }

    /* Actions */
    .release-notes-actions {
        flex-wrap: wrap;
        gap: var(--space-2);
        margin-top: auto;
        padding-top: var(--space-2);
        align-items: flex-end;
    }

    .release-notes-actions .btn {
        width: auto;
        justify-content: center;
        display: flex;
        align-items: center;
    }

    /* Footer link */
    .release-notes-footer {
        justify-content: flex-end;
        padding-top: var(--space-2);
    }
}
</style>
